import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react'
import { Icon } from '../icons/Icon.jsx'
import styles from './ui.module.css'

// Toasts confirm optimistic actions ("Deleted · Undo") and report rollbacks
// ("Couldn't rename — changes undone").

const ToastContext = createContext(null)

let nextId = 1

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([])
  const timers = useRef(new Map())

  const dismiss = useCallback((id) => {
    setToasts((list) => list.filter((t) => t.id !== id))
    clearTimeout(timers.current.get(id))
    timers.current.delete(id)
  }, [])

  const show = useCallback(
    ({ message, tone = 'neutral', action, duration = tone === 'error' ? 7000 : 4500 }) => {
      const id = nextId++
      setToasts((list) => [...list.slice(-3), { id, message, tone, action }])
      timers.current.set(
        id,
        setTimeout(() => dismiss(id), duration),
      )
      return id
    },
    [dismiss],
  )

  const api = useMemo(
    () => ({
      show,
      dismiss,
      success: (message, options) => show({ message, tone: 'neutral', ...options }),
      error: (message, options) => show({ message, tone: 'error', ...options }),
    }),
    [show, dismiss],
  )

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div className={styles.toasts} role="status" aria-live="polite">
        {toasts.map((toast) => (
          <div key={toast.id} className={`${styles.toast} ${toast.tone === 'error' ? styles.toastError : ''}`}>
            <span>{toast.message}</span>
            {toast.action ? (
              <button
                type="button"
                className={styles.toastAction}
                onClick={() => {
                  toast.action.onClick()
                  dismiss(toast.id)
                }}
              >
                {toast.action.label}
              </button>
            ) : null}
            <button type="button" className={styles.toastClose} aria-label="Dismiss" onClick={() => dismiss(toast.id)}>
              <Icon name="close" size={12} strokeWidth={1.8} />
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  )
}

export function useToast() {
  const ctx = useContext(ToastContext)
  if (!ctx) throw new Error('useToast must be used inside <ToastProvider>')
  return ctx
}
